import { useState } from 'react';
import type { ResponseAssetFeature } from '@tsr/shared-types';
import { AssetMedia } from './AssetMedia';
import { classifyAsset } from './assetStyles';
import { OC_CATEGORY_LABEL, renderLucideToDataUrl } from './iconRegistry';
import {
  CLASS_BEST_FOR,
  READINESS_LABEL,
  TIME_TO_DEPLOY_LABEL,
  capacityFor,
  type CapacityProfile,
} from './dispatchCapacity';
import { lifecycleStateStyle } from './styles/lifecycleStates';

/** Asset classes that get the dispatch-coordination view (V.3 §6; R.1.5 base-depot
 *  polish, Task #22 — wildfire/tanker bases + the other operational classes now
 *  route through the readiness/best-use surface instead of the bare detail panel). */
const DISPATCH_CLASSES = new Set([
  'illustrative_marker',
  'umbrella_org',
  'fire_station',
  'medical_facility',
  'aviation_base',
  'marine_vessel',
  'wildfire_base',
  'volunteer_chapter',
  'utility_response',
  'specialty_team',
]);

/** True when an asset should render through DispatchCapacityPanel. */
export function isDispatchAsset(asset: ResponseAssetFeature): boolean {
  return asset.properties.asset_class !== null && DISPATCH_CLASSES.has(asset.properties.asset_class);
}

/** Small Lucide glyph as an <img> (reuses the iconRegistry raster cache). */
function GlyphImg({ name, color, size = 15 }: { name: string; color: string; size?: number }): JSX.Element {
  const url = renderLucideToDataUrl(name, { color, size });
  if (!url) return <span className="inline-block w-3.5 h-3.5 rounded-sm" style={{ backgroundColor: color }} aria-hidden />;
  return <img src={url} width={size} height={size} alt="" aria-hidden className="shrink-0" />;
}

function ReadinessTiers({ readiness }: { readiness: CapacityProfile['readiness'] }): JSX.Element {
  const tiers: Array<[number, number]> = [
    [24, readiness[0]],
    [48, readiness[1]],
    [72, readiness[2]],
  ];
  return (
    <ul className="space-y-1" data-testid="readiness-tiers">
      {tiers.map(([hours, count]) => (
        <li key={hours} className="flex items-center gap-2 text-sm">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" aria-hidden />
          <span className="font-mono tabular-nums text-slate-100">{count.toLocaleString()}</span>
          <span className="text-slate-400">ready in {hours} hours</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Operator-facing dispatch-coordination view for response assets (V.3 §6).
 * Surfaces crew/volunteer readiness, equipment, "best deployed for" and the
 * request/report actions, then embeds the standard asset detail below. For
 * Ontario Corps illustrative assets the readiness/equipment figures are
 * illustrative (banner shown); real operational classes show class capabilities
 * without fabricated personnel counts.
 */
export function DispatchCapacityPanel({ asset }: { asset: ResponseAssetFeature }): JSX.Element {
  const p = asset.properties;
  const style = classifyAsset(p);
  const profile = capacityFor(p.external_id);
  const lifecycle = lifecycleStateStyle(p.lifecycle_state);
  const categoryLabel = p.oc_category ? OC_CATEGORY_LABEL[p.oc_category] : undefined;
  // Best-use guidance: prefer the asset's real capability tags (R.4.1), then the
  // illustrative OC profile, then the per-class default.
  const bestFor =
    (p.capability_tags.length > 0 ? p.capability_tags : undefined) ??
    profile?.bestFor ??
    (p.asset_class ? CLASS_BEST_FOR[p.asset_class] : undefined);
  // Real readiness fields (omit the noisy "unknown" / absent values).
  const readinessLabel =
    p.readiness && p.readiness !== 'unknown' ? (READINESS_LABEL[p.readiness] ?? p.readiness) : null;
  const deployLabel = p.time_to_deploy_band
    ? (TIME_TO_DEPLOY_LABEL[p.time_to_deploy_band] ?? p.time_to_deploy_band)
    : null;

  const [ack, setAck] = useState<string | null>(null);

  return (
    <div className="px-4 py-4 space-y-4" data-testid="dispatch-panel">
      {p.illustrative && (
        <div
          data-testid="illustrative-banner"
          className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
        >
          <span className="font-semibold uppercase tracking-wider">Illustrative</span> — capacity
          figures are example data pending a data partnership with MEPR.
        </div>
      )}

      {/* Header: name + parent org, accented by the asset's role colour. */}
      <div className="border-l-2 pl-3" style={{ borderColor: style.color }}>
        <h2 className="text-base font-semibold leading-tight">{p.name}</h2>
        <p className="text-xs text-slate-400 mt-0.5">
          <span className="inline-block px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
            {categoryLabel ?? style.kind}
          </span>
          {p.operator_org && <span className="ml-2">{p.operator_org}</span>}
        </p>
      </div>

      {/* Status — the operator-facing badge reads the IMS lifecycle_state (Task #93),
          with the real readiness / time-to-deploy bands beside it when known. */}
      <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-slate-400">Status</span>
        <span
          data-testid="lifecycle-state-badge"
          className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded border"
          style={{
            backgroundColor: `${lifecycle.color}22`,
            color: lifecycle.color,
            borderColor: `${lifecycle.color}66`,
          }}
        >
          {lifecycle.label}
        </span>
        {readinessLabel && (
          <span data-testid="asset-readiness" className="text-[11px] text-slate-300">
            · {readinessLabel}
          </span>
        )}
        {deployLabel && <span className="text-[11px] text-slate-400">· {deployLabel}</span>}
      </div>

      {profile && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
            Volunteer / crew availability
          </h3>
          <ReadinessTiers readiness={profile.readiness} />
        </section>
      )}

      {profile && profile.equipment.length > 0 && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Equipment & resources</h3>
          <ul className="space-y-1.5" data-testid="dispatch-equipment">
            {profile.equipment.map((e) => (
              <li key={e.label} className="flex items-center gap-2 text-sm">
                <GlyphImg name={e.icon} color={style.color} />
                <span className="text-slate-300 flex-1">{e.label}</span>
                <span className="font-mono tabular-nums text-slate-100">{e.count}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {bestFor && bestFor.length > 0 && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Best deployed for</h3>
          <div className="flex flex-wrap gap-1.5" data-testid="dispatch-bestfor">
            {bestFor.map((b) => (
              <span key={b} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-200">
                {b}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Actions */}
      <section className="border-t border-slate-800 pt-3 space-y-2">
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="request-deployment"
            onClick={() => setAck('Deployment request queued — illustrative (no live dispatch).')}
            className="flex-1 text-sm font-medium py-1.5 rounded bg-sky-600 hover:bg-sky-500 text-white"
          >
            Request deployment
          </button>
          <button
            type="button"
            data-testid="send-report"
            onClick={() => setAck('Situation report drafted — illustrative (no live channel).')}
            className="flex-1 text-sm font-medium py-1.5 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
          >
            Send report
          </button>
        </div>
        {ack && (
          <p data-testid="dispatch-ack" className="text-[11px] text-sky-300/90">
            {ack}
          </p>
        )}
      </section>

      {/* Standard asset detail (Details / aircraft / linked children). */}
      <div className="border-t border-slate-800 pt-2">
        <AssetMedia asset={asset} variant="embedded" />
      </div>
    </div>
  );
}
